import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = [
    "videoContainer",
    "speakerVideo",
    "galleryContainer",
    "localVideo",
    "viewToggleButton",
    "muteButton",
    "videoToggleButton",
    "endButton",
    "status"
  ]

  static values = {
    roomToken: String,
    userName: String
  }

  connect() {
    this.janus = null
    this.session = null
    this.handle = null
    this.roomId = null
    this.localStream = null
    this.participants = new Map() // janus_session_id -> { element, stream, audioLevel }
    this.currentSpeakerId = null
    this.viewMode = "speaker" // "speaker" или "gallery"
    this.channel = null
    this.isMuted = false
    this.isVideoEnabled = true
    this.audioLevelInterval = null
    this.isJoinedAsPublisher = false // Флаг для отслеживания присоединения как publisher
    this.pendingSubscriptions = new Map() // publisherId -> { publisher, jsep }

    this.connectToChannel()

    // Ждем загрузки скриптов (adapter.js и janus.js) перед инициализацией
    // Скрипты загружаются через content_for :head, даем им время на загрузку
    this.waitForScripts().then(() => {
      this.initializeJanus()
    }).catch((error) => {
      console.error("Не удалось загрузить необходимые скрипты:", error)
      this.updateStatus("Ошибка загрузки скриптов")
    })
  }

  waitForScripts() {
    return new Promise((resolve, reject) => {
      // Проверяем наличие adapter и Janus
      if ((typeof adapter !== "undefined" || typeof window.adapter !== "undefined") &&
          (typeof Janus !== "undefined" || typeof window.Janus !== "undefined")) {
        console.log("Все необходимые скрипты загружены")
        resolve()
        return
      }

      // Ждем загрузки скриптов (максимум 10 секунд)
      let attempts = 0
      const maxAttempts = 100
      const checkInterval = setInterval(() => {
        attempts++
        const hasAdapter = typeof adapter !== "undefined" || typeof window.adapter !== "undefined"
        const hasJanus = typeof Janus !== "undefined" || typeof window.Janus !== "undefined"

        if (hasAdapter && hasJanus) {
          clearInterval(checkInterval)
          console.log("Все необходимые скрипты загружены")
          resolve()
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval)
          const missing = []
          if (!hasAdapter) missing.push("adapter")
          if (!hasJanus) missing.push("Janus")
          reject(new Error(`Не удалось загрузить: ${missing.join(", ")}`))
        }
      }, 100)
    })
  }

  disconnect() {
    this.cleanup()
  }

  connectToChannel() {
    if (!this.roomTokenValue) {
      console.error("Токен комнаты не указан")
      return
    }

    this.consumer = createConsumer()
    this.channel = this.consumer.subscriptions.create(
      {
        channel: "CallRoomChannel",
        room_token: this.roomTokenValue
      },
      {
        connected: () => {
          console.log("Подключено к ActionCable каналу")
        },
        disconnected: () => {
          console.log("Отключено от ActionCable канала")
        },
        received: (data) => {
          this.handleChannelMessage(data)
        }
      }
    )
  }

  async initializeJanus() {
    try {
      // Получаем конфигурацию Janus от сервера
      const response = await fetch(`/api/janus/${this.roomTokenValue}/create_session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const config = await response.json()
      this.sessionId = config.session_id
      this.handleId = config.handle_id
      this.roomId = config.room_id
      this.janusUrl = config.janus_url

      // Используем window.Janus если глобальный Janus недоступен
      const JanusLib = typeof Janus !== "undefined" ? Janus : window.Janus

      if (!JanusLib) {
        throw new Error("Janus SDK не загружен")
      }

      JanusLib.init({
        debug: "all",
        callback: () => {
          this.createJanusSession()
        }
      })
    } catch (error) {
      console.error("Ошибка инициализации Janus:", error)
      this.updateStatus("Ошибка подключения к серверу")
    }
  }

  createJanusSession() {
    // Используем window.Janus если глобальный Janus недоступен
    const JanusLib = typeof Janus !== "undefined" ? Janus : window.Janus

    if (!JanusLib) {
      console.error("Janus SDK не доступен")
      this.updateStatus("Ошибка: Janus SDK не загружен")
      return
    }

    const janus = new JanusLib({
      server: this.janusUrl,
      success: () => {
        console.log("Janus сессия создана")
        this.janus = janus
        this.attachToVideoRoom()
      },
      error: (error) => {
        console.error("Ошибка создания Janus сессии:", error)
        this.updateStatus("Ошибка подключения к Janus")
      },
      destroyed: () => {
        console.log("Janus сессия уничтожена")
      }
    })
  }

  attachToVideoRoom() {
    this.janus.attach({
      plugin: "janus.plugin.videoroom",
      opaqueId: `user-${this.sessionId}`,
      success: (pluginHandle) => {
        console.log("Подключено к videoroom плагину")
        this.handle = pluginHandle
        this.joinRoom()
      },
      error: (error) => {
        console.error("Ошибка подключения к videoroom:", error)
        this.updateStatus("Ошибка подключения к комнате")
      },
      iceState: (state) => {
        console.log("ICE состояние:", state)
      },
      webrtcState: (on) => {
        console.log("WebRTC состояние:", on ? "up" : "down")
      },
      onmessage: (msg, jsep) => {
        this.handleJanusMessage(msg, jsep)
      },
      onremotetrack: (track, mid, on) => {
        this.handleRemoteTrack(track, mid, on)
      }
    })
  }

  async joinRoom() {
    // Проверяем, не присоединились ли уже
    if (this.isJoinedAsPublisher) {
      console.log("Уже присоединены как publisher, пропускаем")
      return
    }

    // Получаем локальный поток
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      })

      if (this.hasLocalVideoTarget) {
        this.localVideoTarget.srcObject = this.localStream
      }

      // Присоединяемся к комнате как publisher
      const register = {
        request: "join",
        room: this.roomId,
        ptype: "publisher",
        display: this.userNameValue
      }

      this.handle.send({ message: register })
    } catch (error) {
      console.error("Ошибка получения медиа потока:", error)
      this.updateStatus("Ошибка доступа к камере/микрофону")
    }
  }

  handleJanusMessage(msg, jsep) {
    console.log("Получено сообщение от Janus:", msg, "JSEP:", jsep)

    if (msg.error) {
      // Игнорируем ошибку "Already in as a publisher" если мы уже присоединились
      if (msg.error_code === 425 && msg.error === "Already in as a publisher on this handle" && this.isJoinedAsPublisher) {
        console.log("Уже присоединены как publisher, это нормально")
        return
      }
      console.error("Ошибка от Janus:", msg.error)
      return
    }

    if (msg.videoroom === "joined") {
      console.log("Присоединились к комнате как publisher")
      this.isJoinedAsPublisher = true
      // Если есть JSEP, обрабатываем его
      if (jsep) {
        this.handle.handleRemoteJsep({ jsep: jsep })
      }
      this.publishLocalStream()
    }

    if (msg.videoroom === "event") {
      if (msg.publishers) {
        // Новые publishers в комнате
        this.subscribeToPublishers(msg.publishers)
      }

      if (msg.leaving) {
        // Участник покинул комнату
        this.removeParticipant(msg.leaving)
      }

      if (msg.unpublished) {
        // Участник прекратил публикацию
        this.removeParticipant(msg.unpublished)
      }
    }

    // Обрабатываем JSEP для подписок
    if (jsep && msg.videoroom === "event") {
      if (msg.subscribed) {
        // Это ответ на подписку
        const publisherId = msg.id || msg.feed
        if (publisherId && this.pendingSubscriptions.has(publisherId)) {
          const subscription = this.pendingSubscriptions.get(publisherId)
          this.pendingSubscriptions.delete(publisherId)
          this.processSubscriptionAnswer(publisherId, jsep)
        }
      } else if (msg.configured) {
        // Это ответ на configure для публикации
        this.handle.handleRemoteJsep({ jsep: jsep })
      }
    }
  }

  publishLocalStream() {
    // Адаптивное качество видео в зависимости от режима
    const videoConstraints = {
      width: { ideal: 1280, max: 1920 },
      height: { ideal: 720, max: 1080 },
      frameRate: { ideal: 30, max: 60 }
    }

    this.handle.createOffer({
      media: {
        audioRecv: false,
        videoRecv: false,
        audioSend: true,
        videoSend: true,
        video: videoConstraints
      },
      success: (jsep) => {
        const publish = {
          request: "configure",
          audio: true,
          video: true,
          bitrate: 1280000 // 1.28 Mbps для хорошего качества
        }
        this.handle.send({ message: publish, jsep: jsep })
      },
      error: (error) => {
        console.error("Ошибка создания оффера:", error)
      }
    })
  }

  subscribeToPublishers(publishers) {
    publishers.forEach((publisher) => {
      // Пропускаем себя - сравниваем по session_id
      const publisherId = publisher.id || publisher.session_id
      if (publisherId === this.sessionId) {
        return
      }

      // Проверяем, не подписаны ли уже на этого участника
      if (this.participants.has(publisherId)) {
        return
      }

      this.subscribeToPublisher(publisher)
    })
  }

  subscribeToPublisher(publisher) {
    const publisherId = publisher.id || publisher.session_id

    // Сохраняем информацию о подписке
    this.pendingSubscriptions.set(publisherId, { publisher })

    const subscribe = {
      request: "join",
      room: this.roomId,
      ptype: "subscriber",
      feed: publisherId
    }

    this.handle.send({
      message: subscribe,
      success: (jsep) => {
        // JSEP может прийти здесь (в некоторых версиях Janus) или в handleJanusMessage
        if (jsep) {
          // Если JSEP пришел здесь, обрабатываем сразу
          this.processSubscriptionAnswer(publisherId, jsep)
        }
        // Если JSEP не пришел здесь, он придет в handleJanusMessage с msg.subscribed
      },
      error: (error) => {
        console.error("Ошибка подписки на publisher:", error)
        this.pendingSubscriptions.delete(publisherId)
      }
    })
  }

  processSubscriptionAnswer(publisherId, jsep) {
    if (!jsep) {
      console.error("JSEP не предоставлен для подписки на publisher:", publisherId)
      this.pendingSubscriptions.delete(publisherId)
      return
    }

    this.handle.createAnswer({
      jsep: jsep,
      media: { audioRecv: true, videoRecv: true, audioSend: false, videoSend: false },
      success: (answerJsep) => {
        const start = { request: "start", room: this.roomId }
        this.handle.send({ message: start, jsep: answerJsep })
      },
      error: (error) => {
        console.error("Ошибка создания answer:", error)
        this.pendingSubscriptions.delete(publisherId)
      }
    })
  }

  handleRemoteTrack(track, mid, on) {
    console.log("Удаленный трек:", track, mid, on)

    if (!on || !track) {
      return
    }

    // Используем mid как идентификатор участника, или создаем новый
    const participantId = mid || track.id || `participant-${Date.now()}`

    if (!this.participants.has(participantId)) {
      this.participants.set(participantId, {
        element: null,
        stream: null,
        audioLevel: 0,
        audioContext: null,
        analyser: null,
        tracks: []
      })
    }

    const participant = this.participants.get(participantId)

    // Создаем или обновляем поток
    if (!participant.stream) {
      participant.stream = new MediaStream()
    }

    // Добавляем трек в поток, если его еще нет
    const existingTrack = participant.stream.getTracks().find(t => t.id === track.id)
    if (!existingTrack) {
      participant.stream.addTrack(track)
      participant.tracks.push(track)
    }

    // Создаем или обновляем видео элемент для участника
    if (track.kind === "video") {
      this.createParticipantVideo(participantId, participant.stream)
    }

    // Настраиваем анализ аудио для определения говорящего
    if (track.kind === "audio") {
      this.setupAudioAnalysis(participantId, participant.stream)
    }
  }

  createParticipantVideo(participantId, stream) {
    const video = document.createElement("video")
    video.autoplay = true
    video.playsInline = true
    video.srcObject = stream
    video.className = "w-full h-full object-cover"

    const participant = this.participants.get(participantId)
    participant.element = video

    // Добавляем видео в соответствующий контейнер в зависимости от режима
    this.updateVideoDisplay()

    // Уведомляем канал о новом участнике
    if (this.channel) {
      this.channel.send({
        type: "participant_joined",
        janus_session_id: participantId,
        janus_handle_id: this.handleId,
        display_name: this.userNameValue
      })
    }
  }

  setupAudioAnalysis(participantId, stream) {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const analyser = audioContext.createAnalyser()
      const source = audioContext.createMediaStreamSource(stream)

      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.8
      source.connect(analyser)

      const participant = this.participants.get(participantId)
      participant.audioContext = audioContext
      participant.analyser = analyser

      // Начинаем отслеживание уровня аудио
      this.startAudioLevelTracking(participantId)
    } catch (error) {
      console.error("Ошибка настройки анализа аудио:", error)
    }
  }

  startAudioLevelTracking(participantId) {
    if (this.audioLevelInterval) {
      return
    }

    this.audioLevelInterval = setInterval(() => {
      this.participants.forEach((participant, id) => {
        if (!participant.analyser) {
          return
        }

        const dataArray = new Uint8Array(participant.analyser.frequencyBinCount)
        participant.analyser.getByteFrequencyData(dataArray)

        // Вычисляем средний уровень аудио
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length
        participant.audioLevel = average

        // Определяем активного говорящего
        this.updateActiveSpeaker()

        // Отправляем обновление уровня аудио через канал
        if (this.channel && average > 10) {
          this.channel.send({
            type: "audio_level_update",
            janus_session_id: id,
            audio_level: average,
            is_speaking: average > 30
          })
        }
      })
    }, 100) // Обновляем каждые 100мс
  }

  updateActiveSpeaker() {
    let maxLevel = 0
    let newSpeakerId = null

    this.participants.forEach((participant, id) => {
      if (participant.audioLevel > maxLevel && participant.audioLevel > 30) {
        maxLevel = participant.audioLevel
        newSpeakerId = id
      }
    })

    if (newSpeakerId !== this.currentSpeakerId) {
      this.currentSpeakerId = newSpeakerId
      this.updateVideoDisplay()

      // Уведомляем канал об изменении говорящего
      if (this.channel && newSpeakerId) {
        this.channel.send({
          type: "speaker_changed",
          janus_session_id: newSpeakerId
        })
      }
    }
  }

  removeParticipant(participantId) {
    const participant = this.participants.get(participantId)
    if (participant) {
      if (participant.element) {
        participant.element.remove()
      }
      if (participant.audioContext) {
        participant.audioContext.close()
      }
      this.participants.delete(participantId)

      this.updateVideoDisplay()

      // Уведомляем канал
      if (this.channel) {
        this.channel.send({
          type: "participant_left",
          janus_session_id: participantId
        })
      }
    }
  }

  updateVideoDisplay() {
    if (this.viewMode === "speaker") {
      this.showSpeakerView()
    } else {
      this.showGalleryView()
    }
  }

  showSpeakerView() {
    if (!this.hasSpeakerVideoTarget) {
      return
    }

    // Очищаем контейнер
    this.speakerVideoTarget.innerHTML = ""

    // Показываем активного говорящего или локальное видео
    if (this.currentSpeakerId && this.participants.has(this.currentSpeakerId)) {
      const participant = this.participants.get(this.currentSpeakerId)
      if (participant.element) {
        const video = participant.element.cloneNode(true)
        video.className = "w-full h-full object-cover"

        // Высокое качество для активного говорящего
        if (video.srcObject) {
          const stream = video.srcObject
          const videoTrack = stream.getVideoTracks()[0]
          if (videoTrack && videoTrack.getConstraints) {
            videoTrack.applyConstraints({
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              frameRate: { ideal: 60 }
            }).catch(err => console.warn("Не удалось установить высокое качество:", err))
          }
        }

        this.speakerVideoTarget.appendChild(video)
      }
    } else if (this.hasLocalVideoTarget && this.localVideoTarget) {
      const localVideo = this.localVideoTarget.cloneNode(true)
      localVideo.className = "w-full h-full object-cover"
      this.speakerVideoTarget.appendChild(localVideo)
    }

    // Скрываем галерею
    if (this.hasGalleryContainerTarget) {
      this.galleryContainerTarget.classList.add("hidden")
    }
    this.speakerVideoTarget.classList.remove("hidden")
  }

  showGalleryView() {
    if (!this.hasGalleryContainerTarget) {
      return
    }

    // Очищаем контейнер
    this.galleryContainerTarget.innerHTML = ""

    // Виртуализация: отображаем только первые N участников для производительности
    const maxVisibleParticipants = 20 // Максимум видимых участников в галерее
    const participantsArray = Array.from(this.participants.entries())
    const visibleParticipants = participantsArray.slice(0, maxVisibleParticipants)

    // Добавляем локальное видео
    if (this.hasLocalVideoTarget && this.localVideoTarget) {
      const localContainer = document.createElement("div")
      localContainer.className = "relative w-full h-full bg-gray-800 rounded-lg overflow-hidden"
      const localVideo = this.localVideoTarget.cloneNode(true)
      localVideo.className = "w-full h-full object-cover"
      localContainer.appendChild(localVideo)

      // Добавляем имя
      const nameOverlay = document.createElement("div")
      nameOverlay.className = "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent px-2 py-1"
      nameOverlay.innerHTML = `<span class="text-xs font-medium text-white">${this.userNameValue}</span>`
      localContainer.appendChild(nameOverlay)

      this.galleryContainerTarget.appendChild(localContainer)
    }

    // Добавляем видео видимых участников
    visibleParticipants.forEach(([id, participant]) => {
      if (participant.element) {
        const container = document.createElement("div")
        container.className = "relative w-full h-full bg-gray-800 rounded-lg overflow-hidden"
        const video = participant.element.cloneNode(true)
        video.className = "w-full h-full object-cover"

        // Оптимизация: снижаем качество видео для галереи
        if (video.srcObject) {
          const stream = video.srcObject
          const videoTrack = stream.getVideoTracks()[0]
          if (videoTrack && videoTrack.getSettings) {
            const settings = videoTrack.getSettings()
            if (settings.width > 640) {
              videoTrack.applyConstraints({ width: 640, height: 360 })
            }
          }
        }

        container.appendChild(video)

        // Добавляем имя участника
        const nameOverlay = document.createElement("div")
        nameOverlay.className = "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent px-2 py-1"
        nameOverlay.innerHTML = `<span class="text-xs font-medium text-white">Участник</span>`
        container.appendChild(nameOverlay)

        this.galleryContainerTarget.appendChild(container)
      }
    })

    // Если участников больше чем видимых, показываем индикатор
    if (participantsArray.length > maxVisibleParticipants) {
      const moreIndicator = document.createElement("div")
      moreIndicator.className = "relative w-full h-full bg-gray-800 rounded-lg flex items-center justify-center"
      moreIndicator.innerHTML = `
        <div class="text-center text-gray-400">
          <p class="text-2xl font-bold">+${participantsArray.length - maxVisibleParticipants}</p>
          <p class="text-sm">еще участников</p>
        </div>
      `
      this.galleryContainerTarget.appendChild(moreIndicator)
    }

    // Скрываем speaker view
    if (this.hasSpeakerVideoTarget) {
      this.speakerVideoTarget.classList.add("hidden")
    }
    this.galleryContainerTarget.classList.remove("hidden")
  }

  toggleView() {
    this.viewMode = this.viewMode === "speaker" ? "gallery" : "speaker"
    this.updateVideoDisplay()
  }

  toggleMute() {
    if (this.localStream) {
      const audioTracks = this.localStream.getAudioTracks()
      audioTracks.forEach((track) => {
        track.enabled = this.isMuted
      })
      this.isMuted = !this.isMuted
    }
  }

  toggleVideo() {
    if (this.localStream) {
      const videoTracks = this.localStream.getVideoTracks()
      videoTracks.forEach((track) => {
        track.enabled = this.isVideoEnabled
      })
      this.isVideoEnabled = !this.isVideoEnabled
    }
  }

  endCall() {
    this.cleanup()
    if (this.channel) {
      this.channel.unsubscribe()
    }
    window.location.href = "/"
  }

  cleanup() {
    if (this.audioLevelInterval) {
      clearInterval(this.audioLevelInterval)
      this.audioLevelInterval = null
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop())
      this.localStream = null
    }

    this.participants.forEach((participant) => {
      if (participant.audioContext) {
        participant.audioContext.close()
      }
      if (participant.element) {
        participant.element.remove()
      }
    })
    this.participants.clear()
    this.pendingSubscriptions.clear()

    if (this.handle) {
      this.handle.detach()
      this.handle = null
    }

    if (this.janus) {
      this.janus.destroy()
      this.janus = null
    }

    this.isJoinedAsPublisher = false
  }

  handleChannelMessage(data) {
    console.log("Получено сообщение от канала:", data)

    switch (data.type) {
      case "connected":
        console.log("Подключено к комнате, участников:", data.participants?.length || 0)
        break
      case "participant_joined":
        console.log("Новый участник присоединился:", data.participant)
        break
      case "participant_left":
        console.log("Участник покинул комнату:", data.janus_session_id)
        this.removeParticipant(data.janus_session_id)
        break
      case "audio_level_update":
        // Обновляем уровень аудио участника
        if (this.participants.has(data.janus_session_id)) {
          const participant = this.participants.get(data.janus_session_id)
          participant.audioLevel = data.audio_level
          this.updateActiveSpeaker()
        }
        break
      case "speaker_changed":
        console.log("Активный говорящий изменился:", data.janus_session_id)
        this.currentSpeakerId = data.janus_session_id
        if (this.viewMode === "speaker") {
          this.updateVideoDisplay()
        }
        break
    }
  }

  updateStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
    }
  }
}

